import { parseExtendedPath } from "csi/core/extendedPathService";
import csi from "csi/main";
import reflectionHints from "csi/reflectionHints";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import xml from "csi/xml";
import { computed } from "mobx";
import { ATTR_PARENT, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeType, VALUE_TYPE } from "src/c8-platform/type/value";
import { getClassConfig, getSubtypeSetting } from "./reflection";
import ReflectionClass from "./ReflectionClass";
import { ATTR_CURRENT_REVISION, ATTR_LATEST_REVISION, COPY_CATEGORY_NO_COPY, TYPE_ABSTRACT_REVISABLE, TYPE_LOOKUP_ITEM } from "./reflectionConstants";
import { getAttributeByClassNameAndId, getClass, getTypeByIndex } from "./reflectionData";
import { DependsOnInfo, ReflectionAttrData, ReflectionRelationData } from "./reflectionTypes";

export default class ReflectionAttribute {
	private readonly _data: ReflectionAttrData | ReflectionRelationData;
	readonly m_rcOwner: ReflectionClass;
	readonly m_id: string;
	readonly m_flags: number;
	readonly m_rflags: number;
	readonly m_format: string;
	readonly m_copyCategory: string;
	readonly m_type: C8AttributeType;

	/** map of corresponding domain attribute ids by reflection type - sometimes the domain attribute id is different from the reflection attribute id */
	domainAttributeIds: Record<C8NodeType, C8AttributeId> = {};

	/** override hidden flag to ensure attribute is visible */
	forceVisible = false;

	/** indicates if attribute is a reference to a domain secondary class (such as Style.Attributes) */
	secondary = false;

	getId(): string {
		return this.m_id;
	}

	getFormat(): string {
		return this.m_format;
	}

	getOwner(): ReflectionClass {
		return this.m_rcOwner;
	}

	getType(): C8AttributeType {
		return this.m_type;
	}

	getColumnWidth(): string {
		return this.columnWidth;
	}

	get attributeId(): string {
		return this._data[0];
	}

	/** indicates if attribute is an association, aggregation, or composite relation (> 0) or just a plain attribute (0) */
	private get attributeType(): number {
		return this._data[1];
	}

	/** bi-directional relation for this relation */
	get bidirectionalRelation(): ReflectionAttribute | null {
		const pair = this.isRelation ? this._data[15] : null;
		return pair ? getAttributeByClassNameAndId(pair[0], pair[1]) : null;
	}

	/** destination class if attribute is a relation and references a single class */
	get destinationClass(): ReflectionClass | undefined {
		return this.getDestinationClass();
	}

	get valueType(): C8AttributeType {
		const valueTypeIndex = this._data[2];
		return isNullOrUndefined(valueTypeIndex) ? VALUE_TYPE.STRING : getTypeByIndex(valueTypeIndex);
	}

	get initialValue(): any {
		return this._data[3] ?? xml.attributeTypeGetDefaultValue(this.valueType);
	}

	/** indicates if attribute is one of the two relations from master to revision */
	get isRelationToRevision(): boolean {
		const { attributeId, m_rcOwner } = this;
		return m_rcOwner.isDerivedFrom(TYPE_ABSTRACT_REVISABLE) && ((attributeId === ATTR_CURRENT_REVISION) || (attributeId === ATTR_LATEST_REVISION));
	}

	/** indicates if this relation is a reverse relation (reverse relations should not be edited directly) */
	get isReverseRelation(): boolean {
		const { attributeRflags } = this;
		return !!(attributeRflags & xml.ATTR_RFLAG_REVERSE_COMPOSITE) ||
			!!(attributeRflags & xml.ATTR_RFLAG_REVERSE_RELATION) ||
			!!(attributeRflags & xml.ATTR_RFLAG_REVERSE_RELATION_DELAYED);
	}

	/** indicates if attribute is a valid preference path step */
	get isValidPreferencePathRelation(): boolean {
		return this.isEnabled &&
			this.isSingle &&
			this.destinationClass?.isValidPreferencePathDestination;
	}

	get attributeFlags(): number {
		return this._data[4] || 0;
	}

	get attributeRflags(): number {
		return this._data[5] || 0;
	}

	get attributeDisabled(): boolean {
		return !!this._data[6];
	}

	get custom(): boolean {
		return this._data[7];
	}

	get format(): string {
		return this._data[8];
	}

	get columnWidth(): string {
		return this._data[9];
	}

	get expression(): string {
		return this._data[10];
	}

	/** node type of source class of attribute */
	get ownerType(): C8NodeType {
		return this.getOwner().type;
	}

	get attributeValidation(): string {
		return this._data[11];
	}

	get copyCategory(): string {
		return xml.enumGetText(this._data[12]);
	}

	get dependsOn(): string {
		return this.isRelation ? this._data[18] : this._data[13];
	}

	get dependsOnPath(): string {
		return this.isRelation ? null : this._data[14];
	}

	// relationships
	@computed get destinations(): ReflectionClass[] {
		if (this.isRelation) {
			const types: string[] = this._data[13];
			return types?.map((type) => getClass(type)) ?? [];
		}

		return [];
	}

	/** return true if relation whose destination class is a secondary class */
	@computed get isDestinationSecondaryClass(): boolean {
		return this.isRelation && this.destinations.some((rc) => rc.isSecondary);
	}

	get ordered(): boolean {
		return this.isRelation ? this._data[14] : null;
	}

	// maybe UNUSED
	get subtypeAttributeId(): number {
		return this.isRelation ? this._data[16] : null;
	}

	get subtypeName(): string {
		return this.isRelation ? this._data[17] : null;
	}

	constructor(item: ReflectionAttrData | ReflectionRelationData, rcOwner: ReflectionClass) {
		this._data = item;
		this.m_id = this.attributeId;
		this.m_flags = this.attributeFlags;
		this.m_rflags = this.attributeRflags;
		this.m_format = this.format;
		this.m_rcOwner = rcOwner;
		this.m_type = this.valueType;
		this.m_copyCategory = this.copyCategory;
	}

	getDependsOnInfo(): DependsOnInfo | null {
		let info: DependsOnInfo;
		const attrId = this.dependsOn;
		const path = this.dependsOnPath;
		const extendedPath = path ? parseExtendedPath(path) : null;
		const rcOwner = extendedPath ? getClass(extendedPath.getEndType()) : this.m_rcOwner;
		const rca = attrId && rcOwner.getAttribute(attrId, true, false, true);
		if (rca) {
			info = {
				attr: rca,
				path: extendedPath,
			};
		}

		return info || null;
	}

	getExpression(): string {
		return this.expression;
	}

	getValidation(): string {
		return this.attributeValidation;
	}

	/**
	 * determine if attribute is autofilled or not
	 */
	get isAutofillDependentEnum(): boolean {
		return (this.attributeRflags & xml.ATTR_RFLAG_AUTOFILL_DEPENDENT_ENUM) != 0;
	}

	/** determine if lookup item attribute is auto-fill or not */
	get isAutofillLookupItem(): boolean {
		const { subtypeName } = this;

		if (!subtypeName) {
			return false;
		}
		const subtypeNameCnl = getClassConfig(TYPE_LOOKUP_ITEM)?.getSubtypeUrlByName(subtypeName);
		const subTypeAutoFill = subtypeNameCnl ? getSubtypeSetting(subtypeNameCnl, "IsAutofillDependent") : false;
		return subTypeAutoFill;
	}

	get isCustom(): boolean {
		return this.custom;
	}

	get isEnabled(): boolean {
		return !this.attributeDisabled;
	}

	/**
	 * determine if attribute is relationship or not
	 */
	get isRelation(): boolean {
		return this.attributeType > 0;
	}

	get isSecondary(): boolean {
		return this.secondary;
	}

	get isComposite(): boolean {
		return this.attributeType == 3;
	}

	@computed getDependents(): ReflectionAttribute[] {
		// summary: iterate through all attributes in class and determine what attributes depend on this one,
		// and recursively find those that depend on them
		// assign dependent enums here to stop infinite recursion
		let a: ReflectionAttribute[] = [];

		const owner = this.getOwner();

		const attributesWithRelations = [...owner.getAttributes(), ...owner.getRelations()];

		attributesWithRelations
			.forEach((rca) => {
				// TODO: process dependencies with paths
				const depInfo = rca.getDependsOnInfo();
				if (depInfo && (depInfo.attr.m_id == this.m_id)) {
					a.push(rca);
					a = a.concat(rca.getDependents());
				}
			});

		return a;
	}

	getBlankValue(): string | null {
		const type = xml.attributeTypeGetBase(this.m_type) || this.m_type;

		switch (type) {
			case xml.ATTR_ENUM: return xml.enumCreate(xml.enumGetId(this.initialValue));
			case xml.ATTR_STRING: return "";
			case xml.ATTR_CNL:
			case xml.ATTR_REF:
			case xml.ATTR_URL: return csi.EMPTY_URL;
		}

		return null;
	}

	getValue(): any {
		return this.initialValue;
	}

	get isCommentRestricted(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_COMMENT_RESTRICTED) != 0;
	}

	get isCopyable(): boolean {
		return this.m_copyCategory != COPY_CATEGORY_NO_COPY;
	}

	/**
	 * Return true if attribute has continuous or one-time expression
	 */
	get hasActiveExpression(): boolean {
		return (this.isOneTimeExp || this.hasFlag(xml.ATTR_FLAG_HAS_EXPRESSION) || this.hasFlag(xml.ATTR_FLAG_EVAL_EXPRESSION)) && !this.hasFlag(xml.ATTR_FLAG_DISABLE_EXPRESSION);
	}

	/**
	 * Return true if attribute has given instance flag
	 */
	private hasFlag(flag: number): boolean {
		return (this.m_flags === null) ? false : xml.valueHasFlag(this.m_flags, flag);
	}

	/**
	 * Return true if attribute has OOTB Notify constraint, or dynamic notify logic through customer actions
	 */
	get hasNotifyLogic(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_NOTIFY) != 0;
	}

	get isPaticipateInAction(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_PARTICIPATE_IN_ACTION) != 0;
	}

	get isDisableClientExpression(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_DISABLE_CLIENT_EXPR) != 0;
	}

	get isDisableDragEdit(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_DISABLE_DRAG_EDIT) != 0;
	}

	get isHidden(): boolean {
		return !this.forceVisible && ((this.m_rflags & xml.ATTR_RFLAG_HIDDEN) != 0);
	}

	get isOneTimeExp(): boolean {
		return (this.m_flags & xml.ATTR_FLAG_ONE_TIME_EXPRESSION) != 0;
	}

	get isOptional(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_OPTIONAL) != 0;
	}

	get isReadOnly(): boolean {
		return (this.m_flags & xml.ATTR_FLAG_NOEDIT) != 0;
	}

	get isRequired(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_REQUIRED) != 0;
	}

	get isDelayedSearch(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_DELAYED_SEARCH) != 0;
	}

	get isFormOnly(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_EDITING_MASK) == xml.ATTR_RFLAG_FORM_EDITING;
	}

	get isCreateOnly(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_EDITING_MASK) == xml.ATTR_RFLAG_CREATE_EDITING;
	}

	get isEditingDisabled(): boolean {
		return this.attributeDisabled || ((this.m_rflags & xml.ATTR_RFLAG_EDITING_MASK) == xml.ATTR_RFLAG_DISABLE_EDITING);
	}

	get isTableViewOnly(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_EDITING_MASK) == xml.ATTR_RFLAG_TABLE_VIEW_EDITING;
	}

	get isViableMatrix(): boolean {
		return (this.m_rflags & xml.ATTR_RFLAG_VIABLE_MATRIX) != 0;
	}

	get isSingle(): boolean {
		return (this.m_type == xml.ATTR_REF);
	}

	/**
	 * rcSourceHint can be specified to possibly provide the derived class using this relation which
	 * may impact the path mapping calculated below
	 * @param rcSourceHint
	 */
	getDestinationClasses(rcSourceHint?: ReflectionClass): ReflectionClass[] {
		// may impact the path mapping calculated below
		const {
			attributeId,
			destinations,
			domainAttributeIds,
			isComposite,
			isDestinationSecondaryClass,
			m_rcOwner,
		} = this;

		const rc = rcSourceHint?.isDerivedFrom(m_rcOwner) ? rcSourceHint : m_rcOwner;

		if (rc && attributeId) {
			const { domainClass, type } = rc;
			const hint = reflectionHints.getTarget(type, attributeId);
			if (hint) {
				return [getClass(hint)];
			}

			// if there is one concrete destination for relation, then assume it is correct
			const numDestinations = destinations.length;
			const hasConcreteDestination = (numDestinations === 1) && !destinations[0].abstract;

			if (domainClass && destinations.length && !hasConcreteDestination) {
				const { outgoing, primary, secondaries } = domainClass;

				// if attribute is a relation to a secondary class, then use domain model
				const secondaryClass = secondaries[attributeId];
				if (secondaryClass) {
					return [secondaryClass];
				} else if (isDestinationSecondaryClass && isComposite) {
					// a concrete class may not implement all secondary classes that are inherited from an abstract class (i.e. Shape.Spec)
					return [];
				}

				// if class is a secondary class and attribute is parent, then return primary class
				if (rc.isSecondary && (attributeId === ATTR_PARENT)) {
					return [primary];
				}

				// check domain model relation for destination which takes advantage of Navigation.xml
				const domainRelation = outgoing[domainAttributeIds[type] || attributeId];
				if (!domainRelation) {
					// if domain model has no such relation, then it could be that reflection model inherits
					// from a class with the relation, but it is not applicable to a particular domain class
					return [];
				} else if (!isDestinationSecondaryClass) {
					// do not currently use domain model to help with hints for relations targeting a secondary class;
					// the domain model will return the primary class rather than secondary (although it should be possible to
					// determine the secondary class from the primary class if only one)
					const type = domainRelation.getSingleDestinationType();
					if (type) {
						return [getClass(type)];
					}
				}
			}
		}

		return destinations;
	}

	/**
	 * find single destination class of relation
	 * @param rcSourceHint
	 */
	getDestinationClass(rcSourceHint?: ReflectionClass): ReflectionClass | null {
		const c = this.getDestinationClasses(rcSourceHint);

		if (c?.length) {
			const { type } = c[0];

			// in some cases, there can be multiple references, but they are all the same
			if ((c.length > 1) && (csi.locate(c, (rc) => (rc.type != type), 1) > 0)) {
				return null;
			}

			return c[0];
		}

		return null;
	}

	getSourceClasses(rcSourceHint: ReflectionClass): ReflectionClass[] | null {
		// summary: rcSourceHint can be specified to possibly provide the derived class using this relation which
		// may impact the path mapping calculated below
		let rcOwner = this.m_rcOwner;

		if (this.attributeId) {
			var rc = rcOwner;

			if (rcSourceHint && rcSourceHint.isDerivedFrom(rc)) {
				rc = rcSourceHint;
			}

			const type = reflectionHints.getTarget(rcSourceHint.getType(), this.attributeId, true);
			if (type != null) {
				rcOwner = getClass(type);
			}
		}

		return rcOwner ? [rcOwner] : null;
	}

	isAggregate(): boolean {
		return this.attributeType == 2;
	}

	isOrdered(): boolean {
		return this.ordered;
	}

	getSubtypeName(): string {
		return this.subtypeName;
	}
}